import type {
  EngagementAppGroupIdFeatureLimitationsProps,
  FeatureLimitation,
  FeatureLimitationsKey,
  LimitationKeysWithArgs,
  LimitationKeysWithoutArgs,
  LimitDataItemMap,
} from "@dashboardApi/apis/engagementAppGroupIdFeatureLimitations";
import type { UseQueryResult } from "@tanstack/react-query";

import type { FetchRequestError } from "@lib/errors";

import { useEngagementAppGroupIdFeatureLimitationApiQuery } from "@services/useEngagementAppGroupIdFeatureLimitationApiQuery";

/**
 * Retrieve a single feature limitation for a key
 *
 * @param limitKey - string key of the feature limitation
 * @param args - arguments that the feature limitation requires
 * @returns query result containing EngagementAppGroupIdFeatureLimitationsResult
 *
 * @example
 * const { data, isLoading } = useFeatureLimitation("iam_campaigns_limit", args);
 */
// Overload signatures
export function useFeatureLimitation<Key extends LimitationKeysWithArgs>(
  limitKey: Key,
  args: LimitDataItemMap[Key]["args"]
): UseQueryResult<FeatureLimitation<Key>, FetchRequestError<any>>;

export function useFeatureLimitation<Key extends LimitationKeysWithoutArgs>(
  limitKey: Key
): UseQueryResult<FeatureLimitation<Key>, FetchRequestError<any>>;

// Implementation
export function useFeatureLimitation<Key extends FeatureLimitationsKey>(
  limitKey: Key,
  args?: LimitDataItemMap[Key]["args"]
): UseQueryResult<FeatureLimitation<Key>, FetchRequestError<any>> {
  return useEngagementAppGroupIdFeatureLimitationApiQuery<Key>({
    limit_key: limitKey,
    args,
  } as EngagementAppGroupIdFeatureLimitationsProps<Key>);
}
